<template>
  <v-container fluid class="demo-container pa-0">
    <v-card class="mx-auto" elevation="4">
      <v-app-bar flat color="primary" dark>
        <v-toolbar-title class="text-h5 font-weight-bold">
          Panel de Control y Reportes
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="toggleDarkMode">
          <v-icon>{{ darkMode ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
        </v-btn>
        <v-btn icon @click="showTutorial">
          <v-icon>mdi-help-circle</v-icon>
        </v-btn>
        <v-btn icon @click="exportData">
          <v-icon>mdi-export</v-icon>
        </v-btn>
      </v-app-bar>

      <v-tabs
        v-model="activeTab"
        background-color="primary"
        dark
        grow
        slider-color="secondary"
      >
        <v-tab v-for="tab in tabs" :key="tab.id">
          <v-icon left>{{ tab.icon }}</v-icon>
          {{ tab.name }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Pestaña de Dashboard -->
        <v-tab-item key="dashboard">
          <v-card flat>
            <v-card-text>
              <v-row>
                <!-- KPIs -->
                <v-col cols="12" class="mb-4">
                  <v-row>
                    <v-col v-for="(kpi, i) in kpis" :key="i" cols="12" sm="6" md="3">
                      <v-card
                        :color="kpi.color"
                        dark
                        hover
                        class="kpi-card transition-swing"
                      >
                        <v-card-text>
                          <div class="text-h4 font-weight-bold mb-2">
                            {{ kpi.value }}
                          </div>
                          <div class="text-subtitle-1">{{ kpi.title }}</div>
                          <v-icon
                            class="kpi-icon"
                            size="48"
                          >
                            {{ kpi.icon }}
                          </v-icon>
                          <div class="text-caption mt-2">
                            <v-icon small :color="kpi.trend === 'up' ? 'green lighten-1' : 'red lighten-1'">
                              {{ kpi.trend === 'up' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                            </v-icon>
                            {{ kpi.change }}% desde el último mes
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-col>

                <!-- Mapa Principal -->
                <v-col cols="12" md="8">
                  <v-card class="mb-4" elevation="2">
                    <v-card-title class="headline">
                      Mapa de Incidentes
                      <v-spacer></v-spacer>
                      <v-btn-toggle v-model="mapaVista" mandatory dense>
                        <v-btn small value="calor">
                          <v-icon small>mdi-fire</v-icon>
                          Calor
                        </v-btn>
                        <v-btn small value="marcadores">
                          <v-icon small>mdi-map-marker</v-icon>
                          Marcadores
                        </v-btn>
                        <v-btn small value="clusters">
                          <v-icon small>mdi-hexagon-multiple</v-icon>
                          Clusters
                        </v-btn>
                      </v-btn-toggle>
                    </v-card-title>
                    <v-card-text>
                      <div id="main-map" style="height: 400px;"></div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Gráficos Laterales -->
                <v-col cols="12" md="4">
                  <v-card class="mb-4" elevation="2">
                    <v-card-title>Distribución por Tipo</v-card-title>
                    <v-card-text>
                      <apexchart
                        type="donut"
                        height="250"
                        :options="chartOptions.donut"
                        :series="series.donut"
                      ></apexchart>
                    </v-card-text>
                  </v-card>
                  
                  <v-card elevation="2">
                    <v-card-title>Tendencia Mensual</v-card-title>
                    <v-card-text>
                      <apexchart
                        type="area"
                        height="200"
                        :options="chartOptions.area"
                        :series="series.area"
                      ></apexchart>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- Pestaña de Análisis -->
        <v-tab-item key="analisis">
          <v-card flat>
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <v-card elevation="2">
                    <v-card-title>
                      Análisis Comparativo
                      <v-spacer></v-spacer>
                      <v-select
                        v-model="periodoAnalisis"
                        :items="periodos"
                        label="Periodo"
                        dense
                        outlined
                        hide-details
                        class="period-select"
                      ></v-select>
                    </v-card-title>
                    <v-card-text>
                      <apexchart
                        type="bar"
                        height="350"
                        :options="chartOptions.comparativo"
                        :series="series.comparativo"
                      ></apexchart>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- Pestaña de Reportes -->
        <v-tab-item key="reportes">
          <v-card flat>
            <v-card-text>
              <v-row>
                <!-- Filtros de Reportes -->
                <v-col cols="12">
                  <v-card elevation="2" class="mb-4">
                    <v-card-title>Generar Reporte Personalizado</v-card-title>
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" md="3">
                          <v-select
                            v-model="reporteTipo"
                            :items="tiposReporte"
                            label="Tipo de Reporte"
                            outlined
                            dense
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-menu
                            v-model="fechaMenu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                v-model="reporteFecha"
                                label="Fecha Inicial"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                                outlined
                                dense
                                prepend-icon="mdi-calendar"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="reporteFecha"
                              @input="fechaMenu = false"
                            ></v-date-picker>
                          </v-menu>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-select
                            v-model="reporteZona"
                            :items="zonas"
                            label="Zona"
                            outlined
                            dense
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="3" class="d-flex align-center">
                          <v-btn color="primary" block @click="generarReporte">
                            <v-icon left>mdi-file-document</v-icon>
                            Generar Reporte
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
                
                <!-- Reportes Recientes -->
                <v-col cols="12" md="6">
                  <v-card elevation="2">
                    <v-card-title>Reportes Recientes</v-card-title>
                    <v-card-text class="pa-0">
                      <v-list two-line>
                        <v-list-item v-for="(reporte, index) in reportesRecientes" :key="index">
                          <v-list-item-avatar>
                            <v-icon :color="reporte.color">{{ reporte.icon }}</v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>{{ reporte.titulo }}</v-list-item-title>
                            <v-list-item-subtitle>{{ reporte.fecha }}</v-list-item-subtitle>
                          </v-list-item-content>
                          <v-list-item-action>
                            <v-btn icon @click="verReporte(reporte)">
                              <v-icon color="primary">mdi-eye</v-icon>
                            </v-btn>
                          </v-list-item-action>
                        </v-list-item>
                      </v-list>
                    </v-card-text>
                  </v-card>
                </v-col>
                
                <!-- Plantillas de Reportes -->
                <v-col cols="12" md="6">
                  <v-card elevation="2">
                    <v-card-title>Plantillas Disponibles</v-card-title>
                    <v-card-text>
                      <v-row>
                        <v-col v-for="(plantilla, index) in plantillas" :key="index" cols="12" sm="6">
                          <v-card outlined hover class="plantilla-card">
                            <v-card-text class="d-flex align-center">
                              <v-icon size="36" :color="plantilla.color" class="mr-3">{{ plantilla.icon }}</v-icon>
                              <div>
                                <div class="font-weight-bold">{{ plantilla.nombre }}</div>
                                <div class="text-caption">{{ plantilla.descripcion }}</div>
                              </div>
                            </v-card-text>
                            <v-card-actions>
                              <v-btn text small color="primary" @click="usarPlantilla(plantilla)">
                                <v-icon small left>mdi-content-copy</v-icon>
                                Usar
                              </v-btn>
                            </v-card-actions>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- Pestaña de Predicciones -->
        <v-tab-item key="predicciones">
          <v-card flat>
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <v-alert
                    color="info"
                    border="left"
                    colored-border
                    elevation="2"
                    class="mb-4"
                  >
                    <div class="text-h6">Simulador de Escenarios Predictivos</div>
                    <div>Utilice esta herramienta para simular diferentes escenarios y obtener predicciones basadas en datos históricos y patrones identificados.</div>
                  </v-alert>
                </v-col>
                
                <!-- Configuración de Simulación -->
                <v-col cols="12" md="4">
                  <v-card elevation="2">
                    <v-card-title>Parámetros de Simulación</v-card-title>
                    <v-card-text>
                      <v-slider
                        v-model="simulacionParams.patrullaje"
                        label="Nivel de Patrullaje"
                        thumb-label="always"
                        min="0"
                        max="100"
                        color="primary"
                      ></v-slider>
                      
                      <v-slider
                        v-model="simulacionParams.camaras"
                        label="Cobertura de Cámaras"
                        thumb-label="always"
                        min="0"
                        max="100"
                        color="primary"
                      ></v-slider>
                      
                      <v-slider
                        v-model="simulacionParams.respuesta"
                        label="Tiempo de Respuesta (min)"
                        thumb-label="always"
                        min="1"
                        max="30"
                        color="primary"
                      ></v-slider>
                      
                      <v-select
                        v-model="simulacionParams.zona"
                        :items="zonas"
                        label="Zona a Simular"
                        outlined
                        dense
                      ></v-select>
                      
                      <v-btn color="primary" block @click="simularEscenario" class="mt-4">
                        <v-icon left>mdi-play</v-icon>
                        Ejecutar Simulación
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>
                
                <!-- Resultados de Simulación -->
                <v-col cols="12" md="8">
                  <v-card elevation="2">
                    <v-card-title>
                      Resultados de la Simulación
                      <v-spacer></v-spacer>
                      <v-chip color="primary" outlined>Escenario #{{ simulacionActual }}</v-chip>
                    </v-card-title>
                    <v-card-text>
                      <v-row>
                        <v-col cols="12">
                          <apexchart
                            type="line"
                            height="300"
                            :options="chartOptions.simulacion"
                            :series="series.simulacion"
                          ></apexchart>
                        </v-col>
                        
                        <v-col cols="12">
                          <v-divider class="my-2"></v-divider>
                          <div class="text-h6 mb-2">Conclusiones de la Simulación</div>
                          <v-alert
                            v-for="(conclusion, index) in conclusionesSimulacion"
                            :key="index"
                            :color="conclusion.color"
                            border="left"
                            colored-border
                            dense
                            class="mb-2"
                          >
                            <div class="font-weight-bold">{{ conclusion.titulo }}</div>
                            <div>{{ conclusion.descripcion }}</div>
                          </v-alert>
                        </v-col>
                      </v-row>
                    </v-card-text>
                    <v-card-actions>
                      <v-btn text color="primary" @click="guardarSimulacion">
                        <v-icon left>mdi-content-save</v-icon>
                        Guardar Escenario
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="compararEscenarios">
                        <v-icon left>mdi-compare</v-icon>
                        Comparar Escenarios
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- Diálogo de Exportación -->
    <v-dialog v-model="dialogExport" max-width="500">
      <v-card>
        <v-card-title>Exportar Datos</v-card-title>
        <v-card-text>
          <v-radio-group v-model="exportFormat">
            <v-radio label="PDF" value="pdf"></v-radio>
            <v-radio label="Excel" value="excel"></v-radio>
            <v-radio label="CSV" value="csv"></v-radio>
          </v-radio-group>
          <v-checkbox v-model="includeCharts" label="Incluir gráficos"></v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogExport = false">Cancelar</v-btn>
          <v-btn color="primary" @click="downloadReport">Descargar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'DemoView',
  components: {
    apexchart: VueApexCharts
  },
  data() {
    return {
      darkMode: false,
      activeTab: 0,
      tabs: [
        { id: 'dashboard', name: 'Dashboard', icon: 'mdi-view-dashboard' },
        { id: 'analisis', name: 'Análisis', icon: 'mdi-chart-bar' },
        { id: 'reportes', name: 'Reportes', icon: 'mdi-file-document' },
        { id: 'predicciones', name: 'Predicciones', icon: 'mdi-crystal-ball' }
      ],
      mapaVista: 'calor',
      periodoAnalisis: 'Último mes',
      periodos: ['Último mes', 'Último trimestre', 'Último año', 'Personalizado'],
      dialogExport: false,
      exportFormat: 'pdf',
      includeCharts: true,
      map: null,
      
      // Datos para reportes
      reporteTipo: 'Incidentes',
      tiposReporte: ['Incidentes', 'Operativos', 'Recursos', 'Estadístico'],
      reporteFecha: new Date().toISOString().substr(0, 10),
      fechaMenu: false,
      reporteZona: 'Todas',
      zonas: ['Todas', 'Capital', 'Rawson', 'Chimbas', 'Rivadavia', 'Santa Lucía', 'Pocito'],
      
      reportesRecientes: [
        { titulo: 'Informe Mensual de Incidentes', fecha: '15/03/2025', icon: 'mdi-file-chart', color: 'primary' },
        { titulo: 'Reporte de Operativos Especiales', fecha: '10/03/2025', icon: 'mdi-police-badge', color: 'deep-purple' },
        { titulo: 'Estadísticas Trimestrales', fecha: '01/03/2025', icon: 'mdi-chart-bar', color: 'green' },
        { titulo: 'Informe de Recursos Asignados', fecha: '25/02/2025', icon: 'mdi-account-group', color: 'blue' }
      ],
      
      plantillas: [
        { nombre: 'Informe Ejecutivo', descripcion: 'Resumen para autoridades', icon: 'mdi-file-document', color: 'indigo' },
        { nombre: 'Reporte Detallado', descripcion: 'Informe completo con anexos', icon: 'mdi-file-chart', color: 'deep-purple' },
        { nombre: 'Mapa de Calor', descripcion: 'Visualización geográfica', icon: 'mdi-map', color: 'red' },
        { nombre: 'Comparativa Anual', descripcion: 'Tendencias interanuales', icon: 'mdi-chart-timeline-variant', color: 'teal' }
      ],
      
      // Datos para simulación
      simulacionParams: {
        patrullaje: 65,
        camaras: 40,
        respuesta: 8,
        zona: 'Capital'
      },
      simulacionActual: 1,
      conclusionesSimulacion: [
        { titulo: 'Reducción de incidentes', descripcion: 'Se estima una reducción del 18% en los incidentes reportados con los parámetros actuales.', color: 'success' },
        { titulo: 'Punto crítico identificado', descripcion: 'La zona norte de Capital requiere mayor cobertura de cámaras para optimizar resultados.', color: 'warning' },
        { titulo: 'Tiempo de respuesta', descripcion: 'Reducir el tiempo de respuesta a 5 minutos podría mejorar la efectividad en un 12% adicional.', color: 'info' }
      ],
      
      // KPIs
      kpis: [
        { title: 'Incidentes Totales', value: '2,458', icon: 'mdi-alert-circle', color: 'deep-purple', trend: 'down', change: 12 },
        { title: 'Casos Resueltos', value: '1,892', icon: 'mdi-check-circle', color: 'green darken-1', trend: 'up', change: 8 },
        { title: 'Tiempo Respuesta', value: '8.5m', icon: 'mdi-clock-fast', color: 'blue darken-1', trend: 'up', change: 5 },
        { title: 'Alertas Activas', value: '18', icon: 'mdi-bell-ring', color: 'red darken-1', trend: 'down', change: 3 }
      ],
      
      // Series para gráficos
      series: {
        donut: [44, 55, 13, 33],
        area: [{
          name: 'Incidentes',
          data: [30, 40, 45, 50, 49, 60, 70, 91, 125]
        }],
        comparativo: [{
          name: 'Este año',
          data: [44, 55, 57, 56, 61, 58, 63, 60, 66]
        }, {
          name: 'Año anterior',
          data: [76, 85, 101, 98, 87, 105, 91, 114, 94]
        }],
        simulacion: [
          {
            name: 'Escenario Actual',
            data: [28, 25, 26, 24, 20, 22, 19, 15, 18, 15, 12, 10]
          },
          {
            name: 'Escenario Optimizado',
            data: [28, 22, 19, 15, 13, 11, 10, 9, 8, 7, 6, 5]
          }
        ]
      },
      
      // Opciones para gráficos
      chartOptions: {
        donut: {
          chart: {
            type: 'donut',
            animations: {
              enabled: true,
              easing: 'easeinout',
              speed: 800
            }
          },
          labels: ['Hurto', 'Robo', 'Daño', 'Otros'],
          colors: ['#3F51B5', '#2196F3', '#03A9F4', '#00BCD4'],
          legend: {
            position: 'bottom'
          },
          plotOptions: {
            pie: {
              donut: {
                size: '65%'
              }
            }
          },
          responsive: [{
            breakpoint: 480,
            options: {
              chart: {
                width: 200
              },
              legend: {
                position: 'bottom'
              }
            }
          }]
        },
        area: {
          chart: {
            type: 'area',
            toolbar: {
              show: false
            },
            animations: {
              enabled: true
            }
          },
          dataLabels: {
            enabled: false
          },
          stroke: {
            curve: 'smooth',
            width: 2
          },
          colors: ['#3F51B5'],
          fill: {
            type: 'gradient',
            gradient: {
              shadeIntensity: 1,
              opacityFrom: 0.7,
              opacityTo: 0.2
            }
          },
          xaxis: {
            categories: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep']
          }
        },
        comparativo: {
          chart: {
            type: 'bar',
            stacked: false,
            toolbar: {
              show: true
            },
            animations: {
              enabled: true
            }
          },
          plotOptions: {
            bar: {
              horizontal: false,
              columnWidth: '55%',
              borderRadius: 2
            },
          },
          dataLabels: {
            enabled: false
          },
          stroke: {
            show: true,
            width: 2,
            colors: ['transparent']
          },
          colors: ['#3F51B5', '#E91E63'],
          xaxis: {
            categories: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep'],
          },
          yaxis: {
            title: {
              text: 'Cantidad de incidentes'
            }
          },
          fill: {
            opacity: 1
          },
          tooltip: {
            y: {
              formatter: function (val) {
                return val + " incidentes"
              }
            }
          }
        },
        simulacion: {
          chart: {
            type: 'line',
            toolbar: {
              show: false
            },
            animations: {
              enabled: true,
              easing: 'linear',
              dynamicAnimation: {
                speed: 1000
              }
            },
            dropShadow: {
              enabled: true,
              opacity: 0.3,
              blur: 5,
              left: 7,
              top: 7
            }
          },
          colors: ['#FF9800', '#4CAF50'],
          dataLabels: {
            enabled: false
          },
          stroke: {
            curve: 'smooth',
            width: [3, 3],
            dashArray: [0, 0]
          },
          grid: {
            borderColor: '#e7e7e7',
            row: {
              colors: ['#f3f3f3', 'transparent'],
              opacity: 0.5
            },
          },
          markers: {
            size: 6,
            hover: {
              size: 10
            }
          },
          xaxis: {
            categories: ['Mes 1', 'Mes 2', 'Mes 3', 'Mes 4', 'Mes 5', 'Mes 6', 'Mes 7', 'Mes 8', 'Mes 9', 'Mes 10', 'Mes 11', 'Mes 12'],
            title: {
              text: 'Periodo de Simulación'
            }
          },
          yaxis: {
            title: {
              text: 'Incidentes Proyectados'
            },
            min: 0
          },
          legend: {
            position: 'top',
            horizontalAlign: 'right',
            floating: true,
            offsetY: -25,
            offsetX: -5
          }
        }
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initMap();
    });
  },
  watch: {
    activeTab(newVal) {
      if (newVal === 0) {
        this.$nextTick(() => {
          this.initMap();
        });
      }
    }
  },
  methods: {
    initMap() {
      console.log('Intentando inicializar el mapa...');
      setTimeout(() => {
        const mapElement = document.getElementById('main-map');
        if (!mapElement) {
          console.warn('Elemento del mapa no encontrado');
          return;
        }
        
        console.log('Elemento del mapa encontrado, inicializando...');
        
        if (this.map) {
          console.log('Eliminando mapa existente');
          this.map.remove();
        }
        
        const sanJuanCoords = [-31.5375, -68.5364];
        
        try {
          this.map = L.map('main-map').setView(sanJuanCoords, 13);
          
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          }).addTo(this.map);
          
          console.log('Mapa inicializado correctamente');
          
          const markers = [
            { pos: [-31.5375, -68.5364], title: 'Centro', type: 'robo' },
            { pos: [-31.5280, -68.5420], title: 'Norte', type: 'hurto' },
            { pos: [-31.5480, -68.5280], title: 'Este', type: 'daño' },
            { pos: [-31.5320, -68.5520], title: 'Oeste', type: 'robo' },
            { pos: [-31.5580, -68.5350], title: 'Sur', type: 'hurto' }
          ];
          
          markers.forEach(marker => {
            let icon;
            switch(marker.type) {
              case 'robo':
                icon = L.divIcon({
                  className: 'custom-div-icon',
                  html: "<div class='marker-pin red'></div>",
                  iconSize: [30, 42],
                  iconAnchor: [15, 42]
                });
                break;
              case 'hurto':
                icon = L.divIcon({
                  className: 'custom-div-icon',
                  html: "<div class='marker-pin blue'></div>",
                  iconSize: [30, 42],
                  iconAnchor: [15, 42]
                });
                break;
              default:
                icon = L.divIcon({
                  className: 'custom-div-icon',
                  html: "<div class='marker-pin green'></div>",
                  iconSize: [30, 42],
                  iconAnchor: [15, 42]
                });
            }
            
            L.marker(marker.pos, { icon: icon })
              .bindPopup(`<b>${marker.title}</b><br>Tipo: ${marker.type}`)
              .addTo(this.map);
          });
        } catch (error) {
          console.error('Error al inicializar el mapa:', error);
        }
      }, 1000);
    },
    
    toggleDarkMode() {
      this.darkMode = !this.darkMode;
      this.$vuetify.theme.dark = this.darkMode;
    },
    
    showTutorial() {
      alert('Tutorial de uso: Esta función mostraría un recorrido guiado por las funcionalidades principales.');
    },
    
    exportData() {
      this.dialogExport = true;
    },
    
    downloadReport() {
      alert(`Descargando reporte en formato ${this.exportFormat.toUpperCase()}${this.includeCharts ? ' con gráficos' : ''}`);
      this.dialogExport = false;
    },
    
    generarReporte() {
      alert(`Generando reporte de ${this.reporteTipo} para la zona ${this.reporteZona} desde ${this.reporteFecha}`);
    },
    
    verReporte(reporte) {
      alert(`Visualizando reporte: ${reporte.titulo} del ${reporte.fecha}`);
    },
    
    usarPlantilla(plantilla) {
      alert(`Aplicando plantilla: ${plantilla.nombre}`);
      this.reporteTipo = 'Personalizado';
    },
    
    simularEscenario() {
      alert('Ejecutando simulación con los parámetros especificados...');
      this.simulacionActual++;
    },
    
    guardarSimulacion() {
      alert(`Escenario #${this.simulacionActual} guardado correctamente`);
    },
    
    compararEscenarios() {
      alert('Comparando escenarios actuales con históricos...');
    }
  },
  activated() {
    if (this.activeTab === 0) {
      this.$nextTick(() => {
        this.initMap();
      });
    }
  }
}
</script>

<style scoped>
.demo-container {
  background-color: #f5f5f5;
  min-height: 100vh;
}

.kpi-card {
  position: relative;
  overflow: hidden;
  transition: transform 0.3s;
}

.kpi-card:hover {
  transform: translateY(-5px);
}

.kpi-icon {
  position: absolute;
  right: 10px;
  top: 10px;
  opacity: 0.2;
}

.period-select {
  max-width: 150px;
}

.marker-pin {
  width: 30px;
  height: 30px;
  border-radius: 50% 50% 50% 0;
  background: #c30b82;
  position: absolute;
  transform: rotate(-45deg);
  left: 50%;
  top: 50%;
  margin: -15px 0 0 -15px;
}

.marker-pin::after {
  content: '';
  width: 24px;
  height: 24px;
  margin: 3px 0 0 3px;
  background: #fff;
  position: absolute;
  border-radius: 50%;
}

.marker-pin.red {
  background: #F44336;
}

.marker-pin.blue {
  background: #2196F3;
}

.marker-pin.green {
  background: #4CAF50;
}

.plantilla-card {
  transition: all 0.3s ease;
  cursor: pointer;
}

.plantilla-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.v-slider__thumb-label {
  font-weight: bold;
}
</style>
